import {
    EuiButtonIcon,
    EuiFlexGroup,
    EuiFlexItem,
    EuiHealth,
    EuiInMemoryTable,
    EuiLink,
    EuiLoadingSpinner,
    EuiModal,
    EuiModalBody,
    EuiModalHeader,
    EuiModalHeaderTitle,
    EuiSwitch,
    EuiText,
} from "@elastic/eui";
import { DateTime } from "luxon";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router";
import { ShopContext } from "utils/Shop";

import client from "../api/Client";
import { useUpdatePriceMutation } from "../api/react-query/mutations/prices";
import { useDeleteShopPriceMutation } from "../api/react-query/mutations/shop-to-prices";
import { updateShopToPrice, useFetchProductsPagePrices } from "../api/react-query/queries/products-page";
import { UserContext } from "../App";
import { parseInternalProductId } from "../utils/FormUtils";
import { useCurrentShopGroup } from "../utils/PricelistUtils";
import { ModifiedAtColumn } from "../utils/TableColumns";
import { helperNumber } from "../utils/Utils";
import { ColumnPriceForm } from "./ColumnPriceForm";
import { InternalProductIdCell } from "./editors/InternalProductIdCellEditor";
import ConfirmationDialog from "./modals/ConfirmationDialog";
import { PriceHistoryPicker } from "./modals/PriceHistoryPicker";
import { PriceGrid } from "./PriceGrid";

export const makeProductResultsColumns = ({
    field,
    showAdminLinks,
    showEditLink,
    canEdit,
    mode,
    helperNumber,
    loadingRows,
    currentShopGroupId,
    updatePriceMutation,
    setItems,
    setEditRecord,
    getModalPrices,
    setShowPriceHistoryPicker,
    handleRowClick,
    handleAvailabilityToggle,
    handleSwap,
    setDeleteId,
    setConfirmDialogOpen,
}) => {
    const columns = [
        { field: "category_name", name: "Category", sortable: true, width: 95 },

        {
            field,
            name: "Product",
            sortable: true,
            width: 140,
            render: showAdminLinks
                ? (fieldValue, object) =>
                      showEditLink ? (
                          <EuiLink onClick={() => handleRowClick(object)} target="_blank">
                              {fieldValue}
                          </EuiLink>
                      ) : (
                          <span>{fieldValue}</span>
                      )
                : undefined,
        },

        { field: "new", name: "Nieuw", sortable: true, width: 60, render: (v) => <span>{v ? "ja" : "nee"}</span> },

        { field: "description", name: "Beschrijving", sortable: true },

        {
            field: "prices",
            name: "Prijzen",
            sortable: (prices) => {
                const jointPrice = helperNumber + prices.joint;
                const halfPrice = helperNumber + prices.half;
                const onePrice = helperNumber + prices.one;
                const twoFivePrice = helperNumber + prices.two_five;
                const fivePrice = helperNumber + prices.five;
                const piecePrice = helperNumber + prices.piece;
                return [halfPrice, onePrice, twoFivePrice, fivePrice, jointPrice, piecePrice];
            },
            render: (prices, record) => (
                <>
                    <PriceGrid
                        prices={prices}
                        record={record}
                        setEditRecord={setEditRecord}
                        getModalPrices={getModalPrices}
                        mode={mode}
                    />

                    {mode === "cannabis" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <EuiButtonIcon
                                aria-label="edit"
                                iconType="plus"
                                onClick={() => {
                                    setEditRecord(record);
                                    getModalPrices(undefined, undefined, record.internal_product_id);
                                }}
                            />
                            <EuiButtonIcon
                                aria-label="edit"
                                iconType="clock"
                                onClick={() => {
                                    setShowPriceHistoryPicker(true);
                                    setEditRecord(record);
                                }}
                            />
                        </div>
                    )}
                </>
            ),
        },

        {
            field: "internal_product_id",
            name: "Code",
            sortable: true,
            width: 125,
            render: (id, price) => (
                <InternalProductIdCell
                    id={id}
                    price={price}
                    currentShopGroupId={currentShopGroupId}
                    updatePriceMutation={updatePriceMutation}
                    mode={mode}
                />
            ),
        },

        {
            field: "modified_at",
            width: 100,
            name: "Aangepast",
            sortable: ({ modified_at }) => DateTime.fromISO(modified_at),
            render: (modified_at) => <ModifiedAtColumn modifiedAt={modified_at} />,
            align: "right",
        },

        {
            field: "active",
            name: "Voorraad?",
            render: (active) => (
                <EuiHealth color={active ? "success" : "danger"} size="l">
                    {active ? "ja" : "nee"}
                </EuiHealth>
            ),
            align: "right",
            sortable: true,
            width: 80,
        },

        {
            field: "active",
            name: "",
            render: (active, record) => (
                <EuiSwitch
                    aria-label="toggle-availability"
                    label=""
                    checked={active}
                    onChange={() => handleAvailabilityToggle(record)}
                />
            ),
            align: "right",
            width: 60,
        },

        {
            field: "prices",
            name: "",
            render: (prices, record) => (
                <div>
                    <EuiFlexGroup>
                        {mode === "horeca" && !loadingRows.includes(record.order_number) && (
                            <EuiFlexItem>
                                <EuiFlexGroup direction="column">
                                    <EuiFlexItem style={{ margin: 1 }}>
                                        <EuiButtonIcon
                                            disabled={record.order_number === 0}
                                            aria-label="arrowUp"
                                            iconType="arrowUp"
                                            onClick={() => handleSwap(record.id, true, record.order_number)}
                                        />
                                    </EuiFlexItem>
                                    <EuiFlexItem style={{ margin: 1 }}>
                                        <EuiButtonIcon
                                            disabled={record?.hasOwnProperty("isLast") ? record.isLast : true}
                                            aria-label="arrowDown"
                                            iconType="arrowDown"
                                            onClick={() => handleSwap(record.id, false, record.order_number)}
                                        />
                                    </EuiFlexItem>
                                </EuiFlexGroup>
                            </EuiFlexItem>
                        )}

                        {mode === "horeca" && loadingRows.includes(record.order_number) && (
                            <EuiFlexItem>
                                <EuiFlexGroup direction="column">
                                    <EuiFlexItem style={{ margin: 1, marginTop: 10 }}>
                                        <EuiButtonIcon aria-label="arrowUp" iconType="arrowUp" isLoading={true} />
                                    </EuiFlexItem>
                                </EuiFlexGroup>
                            </EuiFlexItem>
                        )}

                        <EuiFlexItem style={{ marginLeft: 30, marginRight: 20 }}>
                            <EuiLink href={canEdit ? `/products/edit-product/${mode}/${record.id}` : undefined}>
                                <EuiButtonIcon aria-label="edit" iconType="pencil" disabled={!canEdit} />
                            </EuiLink>
                        </EuiFlexItem>

                        <EuiFlexItem>
                            <EuiButtonIcon
                                aria-label="trash"
                                iconType="trash"
                                disabled={!canEdit}
                                onClick={() => {
                                    setDeleteId(record.id);
                                    setConfirmDialogOpen(true);
                                }}
                            />
                        </EuiFlexItem>
                    </EuiFlexGroup>
                </div>
            ),
            align: "right",
            width: 170,
        },
    ];

    if (mode === "horeca") {
        columns.splice(1, 0, { field: "order_number", name: "Order Number", sortable: true, width: 110 });
    }

    return columns;
};

export const ProductResults = ({ selectedScreenId, searchIsOpen, mode, refresh, ...props }) => {
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [showPriceHisoryPicker, setShowPriceHistoryPicker] = useState(false);
    const [deleteId, setDeleteId] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingRows, setLoadingRows] = useState([]);

    const { shopId } = useContext(ShopContext);
    const { data: filteredData, isLoading } = useFetchProductsPagePrices(shopId, mode);

    const products = React.useMemo(() => {
        if (!filteredData) return [];

        if (selectedScreenId === "-1") return filteredData;

        return filteredData.filter((price) => price.category_screens?.some((cs) => cs.screen_id === selectedScreenId));
    }, [filteredData, selectedScreenId]);

    const [error, setError] = useState();
    const tableRef = useRef();

    const history = useHistory();
    const user = useContext(UserContext);
    const shopContext = useContext(ShopContext);
    const queryClient = useQueryClient();

    const [edibleJsonPrices, setEdibleJsonPrices] = useState([]);
    const [preRolledJsonPrices, setPreRolledJsonPrices] = useState([]);
    const [cannabisJsonPrices, setCannabisJsonPrices] = useState([]);
    const [internalIdPrefix, setInternalIdPrefix] = useState("000");

    const [actionLabels, setActionLabels] = useState([]);

    const [showModal, setShowModal] = useState(false);

    const [editRecord, setEditRecord] = useState(undefined);
    const [tablePriceKey, setTablePriceKey] = useState(undefined);
    const [tablePriceValue, setTablePriceValue] = useState(undefined);
    const [labelSuffix, setLabelSuffix] = useState("g");
    const currentShopGroup = useCurrentShopGroup();

    const deletePriceMutation = useDeleteShopPriceMutation(shopContext.shopId, mode);
    const updatePriceMutation = useUpdatePriceMutation();

    const getModalPrices = useCallback((priceKey, priceValue, priceId) => {
        return client.get(`/v1/prices?filter=internal_product_id%3A${priceId}`).then((result) => {
            if (result.data.length > 0 && result.data[0]) {
                setCannabisJsonPrices(result.data[0].cannabis);
                setEdibleJsonPrices(result.data[0].edible);
                setPreRolledJsonPrices(result.data[0].pre_rolled_joints);
                setActionLabels(result.data[0].action_labels);
                setInternalIdPrefix(parseInternalProductId(result.data[0].internal_product_id).prefix);
                setTablePriceKey(priceKey);
                setTablePriceValue(priceValue);
                setShowModal(true);
            }
        });
    }, []);

    const handleAvailabilityToggle = useCallback(
        (record) => {
            const now = DateTime.local();
            const modified_at = now.minus({ minutes: now.offset });
            const nextActive = !record.active;

            queryClient.setQueryData(["products", shopId, mode], (oldData) => {
                if (!oldData) return oldData;
                return {
                    ...oldData,
                    prices: oldData.prices.map((p) =>
                        p.id === record.id ? { ...p, active: nextActive, modified_at } : p
                    ),
                };
            });

            // No cache read, no find, no race condition
            client
                .put(`/v1/shops-to-prices/availability/${record.id}`, {
                    active: nextActive,
                })
                .catch((err) => {
                    console.log("Error updating availability", err);
                    queryClient.invalidateQueries(["products", shopContext.shopId, mode]);
                });
        },
        [mode, queryClient, shopContext.shopId, shopId]
    );

    useEffect(() => {
        console.log("Mode", mode);
    }, [mode]);

    const handleDelete = (id) => {
        setLoading(true);
        deletePriceMutation.mutate(id, {
            onSettled: () => {
                setLoading(false);
            },
            onError: (err) => {
                setError(err.message);
            },
        });
    };

    const getMaximumNumbers = (items) => {
        let previousItemOrderNumber = -1;
        let newArray = [];

        for (let i = 0; i < items.length; i++) {
            let newItem = items[i];
            newItem["isLast"] = false;
            if (items[i].order_number < previousItemOrderNumber) {
                newArray[i - 1]["isLast"] = true;
            }
            previousItemOrderNumber = items[i].order_number;
            newArray.push(newItem);
        }
        if (newArray.length >= 1) {
            newArray[items.length - 1]["isLast"] = true;
        }
        return newArray;
    };

    const handleSwap = useCallback(
        (id, moveUp, order_number) => {
            let new_order_number = moveUp ? order_number - 1 : order_number + 1;
            setLoadingRows([order_number, new_order_number]);
            client
                .patch(`/v1/shops-to-prices/swap/${id}?move_up=${moveUp}`)
                .then((result) => {
                    setLoading(false);
                    setLoadingRows([]);
                    queryClient.invalidateQueries(["products", shopContext.shopId, mode]);
                })
                .catch((result) => {
                    const data = result.response;
                    // debugger;
                    if (data.status === 401) {
                        setError(`update resulted in error: ${data.statusText}`);
                        setLoading(false);
                    } else {
                        setError(`update resulted in error ${data.status}: ${data.statusText}`);
                        setLoading(false);
                    }
                    setLoadingRows([]);
                });
        },
        [mode, queryClient, shopContext.shopId]
    );

    const handleRowClick = useCallback(
        (item) => {
            if (item.kind_id !== null) {
                history.push(`/view/cannabis/${item.kind_id}`);
            } else if (item.product_id !== null) {
                history.push(`/view/product/${item.product_id}`);
            }
        },
        [history]
    );
    // TODO: Make not hardcoded
    const showAdminLinks = !user?.profile?.email?.endsWith("@prijslijst.info");
    const showEditLink = user.profile.email !== "theroundabout69@gmail.com";
    const canEdit =
        user.profile.is_superuser ||
        user.profile.roles?.some((role) => role.name === "operator" || role.name === "admin");
    const field = mode === "horeca" ? "product_name" : "kind_name";

    const columns = React.useMemo(
        () =>
            makeProductResultsColumns({
                field,
                showAdminLinks,
                showEditLink,
                canEdit,
                mode,
                helperNumber,
                loadingRows,
                currentShopGroupId: currentShopGroup.id,
                updatePriceMutation,
                // setItems,
                setEditRecord,
                getModalPrices,
                setShowPriceHistoryPicker,
                handleRowClick,
                handleAvailabilityToggle,
                handleSwap,
                setDeleteId,
                setConfirmDialogOpen,
            }),
        [
            field,
            showAdminLinks,
            showEditLink,
            canEdit,
            mode,
            loadingRows,
            currentShopGroup.id,
            updatePriceMutation,
            // setItems,
            setEditRecord,
            getModalPrices,
            setShowPriceHistoryPicker,
            handleRowClick,
            handleAvailabilityToggle,
            handleSwap,
            setDeleteId,
            setConfirmDialogOpen,
        ]
    );

    if (mode === "horeca") {
        columns.splice(1, 0, {
            field: "order_number",
            name: "Order Number",
            sortable: true,
            width: 110,
        });
    }

    const getCustomDefaultSorting = (shopId) => {
        const customMappings = {
            "470f3f5a-e7b9-43a5-bbfd-2ffce8c161e4": { field: field, direction: "asc" },
        };

        return customMappings[shopId] || { field: "category_name", direction: "asc" };
    };

    const sorting = {
        sort: getCustomDefaultSorting(shopContext.shopId),
    };

    const search = {
        box: {
            incremental: false,
            schema: true,
        },
    };

    const handlePriceSubmit = async () => {
        updateShopToPrice(
            currentShopGroup,
            cannabisJsonPrices,
            preRolledJsonPrices,
            edibleJsonPrices,
            editRecord,
            actionLabels,
            shopContext,
            internalIdPrefix
        ).then();
    };

    return (
        <>
            {showModal && (
                <EuiModal onClose={() => setShowModal(false)}>
                    <EuiModalHeader>
                        <EuiModalHeaderTitle>
                            {tablePriceKey ? "Prijs bewerken of verwijderen" : "Prijs toevoegen"}
                        </EuiModalHeaderTitle>
                    </EuiModalHeader>
                    <EuiModalBody>
                        <ColumnPriceForm
                            usedPrice={editRecord}
                            currentLabel={tablePriceKey}
                            tablePriceValue={tablePriceValue}
                            setShowModal={setShowModal}
                            labelSuffix={labelSuffix}
                            handlePriceSubmit={handlePriceSubmit}
                            setLabelSuffix={setLabelSuffix}
                            cannabisJsonPrices={cannabisJsonPrices}
                            edibleJsonPrices={edibleJsonPrices}
                            preRolledJsonPrices={preRolledJsonPrices}
                            setCannabisJsonPrices={setCannabisJsonPrices}
                            setEdibleJsonPrices={setEdibleJsonPrices}
                            setPreRolledJsonPrices={setPreRolledJsonPrices}
                            setActionLabels={setActionLabels}
                        />
                    </EuiModalBody>
                </EuiModal>
            )}
            {showPriceHisoryPicker && (
                <PriceHistoryPicker usedPrice={editRecord} close={() => setShowPriceHistoryPicker(false)} />
            )}
            <ConfirmationDialog
                isOpen={confirmDialogOpen}
                cancel={() => setConfirmDialogOpen(false)}
                confirm={() => {
                    setLoading(true);
                    handleDelete(deleteId);
                    setConfirmDialogOpen(false);
                }}
                question={"Weet je zeker dat je deze wilt wissen?"}
            />
            {isLoading ? (
                <EuiLoadingSpinner size="xl" />
            ) : (
                <EuiInMemoryTable
                    ref={tableRef}
                    loading={loading}
                    layout="auto"
                    items={getMaximumNumbers(products)}
                    rowHeader="description"
                    columns={columns}
                    error={error}
                    sorting={sorting}
                    search={searchIsOpen ? search : null}
                    isSelectable={true}
                />
            )}
            {filteredData?.length === 0 && !isLoading && <EuiText>Er zijn geen producten van dit type.</EuiText>}
        </>
    );
};
